import React, { useCallback, useEffect, useState } from "react";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import Chart from "chart.js/auto";
import { api, getApiBaseUrl } from "./apiClient";
import { formatApiError } from "./apiError";
import { useAuth } from "./AuthContext";
import { useServerDebug } from "./DebugContext";
import "./creditUnionDashboard.css";

function cuRequestParams(f) {
  const p = {};
  if (f.cu_id) p.cu_id = f.cu_id;
  if (f.start) p.start = f.start;
  if (f.end) p.end = f.end;
  if (f.approved) p.approved = f.approved;
  if (f.completed) p.completed = f.completed;
  if (f.employee_q?.trim()) p.employee_q = f.employee_q.trim();
  return p;
}

const chartColors = {
  grid: "rgba(148, 163, 184, 0.12)",
  text: "#94a3b8",
};

const palette = [
  "#2dd4bf",
  "#38bdf8",
  "#a78bfa",
  "#fb7185",
  "#fbbf24",
  "#4ade80",
  "#f472b6",
  "#94a3b8",
];

function asArray(v) {
  return Array.isArray(v) ? v : [];
}

function fmtInt(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString();
}

function fmtHours(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `${Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} h`;
}

function fmtTs(v) {
  if (!v) return "—";
  return String(v).replace("T", " ").slice(0, 19);
}

export default function CreditUnionDashboard({ onBack }) {
  const { user, logout } = useAuth();
  const serverDebug = useServerDebug();
  const apiBase = getApiBaseUrl();

  const [filters, setFilters] = useState({
    cu_id: "",
    start: "",
    end: "",
    approved: "",
    completed: "",
    employee_q: "",
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [creditUnions, setCreditUnions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(null);

  const [summary, setSummary] = useState({
    total_surveys: 0,
    completed_surveys: 0,
    approved_surveys: 0,
    pending_approval: 0,
    unique_employees: 0,
    credit_unions_represented: 0,
    total_hours_reported: 0,
  });
  const [timeline, setTimeline] = useState({ labels: [], data: [] });
  const [approval, setApproval] = useState({ labels: [], data: [] });
  const [hoursByCat, setHoursByCat] = useState({ labels: [], data: [] });
  const [hoursByCu, setHoursByCu] = useState({ labels: [], data: [] });
  const [topActivities, setTopActivities] = useState({ labels: [], data: [] });
  const [surveys, setSurveys] = useState([]);

  const params = useCallback(() => cuRequestParams(filters), [filters]);

  const loadCreditUnions = useCallback(async () => {
    try {
      const { data } = await api.get("/api/cu/credit-unions");
      setCreditUnions(asArray(data));
    } catch {
      setCreditUnions([]);
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const p = params();
    try {
      const [
        sumRes,
        tlRes,
        apRes,
        hcRes,
        hcuRes,
        taRes,
        svRes,
      ] = await Promise.all([
        api.get("/api/cu/summary", { params: p }),
        api.get("/api/cu/surveys-timeline", { params: p }),
        api.get("/api/cu/approval-breakdown", { params: p }),
        api.get("/api/cu/hours-by-category", { params: p }),
        api.get("/api/cu/hours-by-credit-union", { params: p }),
        api.get("/api/cu/top-activities", { params: p }),
        api.get("/api/cu/surveys", { params: { ...p, limit: 150, offset: 0 } }),
      ]);

      const s = sumRes.data && typeof sumRes.data === "object" ? sumRes.data : {};
      setSummary({
        total_surveys: s.total_surveys ?? 0,
        completed_surveys: s.completed_surveys ?? 0,
        approved_surveys: s.approved_surveys ?? 0,
        pending_approval: s.pending_approval ?? 0,
        unique_employees: s.unique_employees ?? 0,
        credit_unions_represented: s.credit_unions_represented ?? 0,
        total_hours_reported: s.total_hours_reported ?? 0,
      });

      const tl = asArray(tlRes.data);
      setTimeline({
        labels: tl.map((r) => String(r.day)),
        data: tl.map((r) => r.count),
      });

      const ap = asArray(apRes.data);
      setApproval({
        labels: ap.map((r) => r.status_label),
        data: ap.map((r) => r.count),
      });

      const hc = asArray(hcRes.data);
      setHoursByCat({
        labels: hc.map((r) => r.category_name),
        data: hc.map((r) => Number(r.total_hours)),
      });

      const hcu = asArray(hcuRes.data);
      setHoursByCu({
        labels: hcu.map((r) => r.cu_name),
        data: hcu.map((r) => Number(r.total_hours)),
      });

      const ta = asArray(taRes.data);
      setTopActivities({
        labels: ta.map(
          (r) => `#${r.activity_number} ${r.activity_description || ""}`.trim(),
        ),
        data: ta.map((r) => Number(r.total_hours)),
      });

      setSurveys(asArray(svRes.data));
    } catch (e) {
      setError(formatApiError(e, serverDebug));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    loadCreditUnions();
  }, [loadCreditUnions]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    Chart.defaults.color = chartColors.text;
    Chart.defaults.borderColor = chartColors.grid;
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  };

  const downloadExport = async (path, filename) => {
    setExporting(filename);
    setError(null);
    try {
      const res = await api.get(path, {
        params: params(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      let msg = formatApiError(e, serverDebug);
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const j = JSON.parse(text);
          msg =
            serverDebug && j.traceback
              ? [j.detail, ...(j.traceback || [])].join("\n")
              : j.detail || text || msg;
        } catch {
          msg = "Export failed";
        }
      }
      setError(msg);
    } finally {
      setExporting(null);
    }
  };

  const lineData = {
    labels: timeline.labels,
    datasets: [
      {
        label: "Surveys started",
        data: timeline.data,
        borderColor: "#2dd4bf",
        backgroundColor: "rgba(45, 212, 191, 0.15)",
        fill: true,
        tension: 0.25,
      },
    ],
  };

  const doughnutData = {
    labels: approval.labels,
    datasets: [
      {
        data: approval.data,
        backgroundColor: approval.labels.map((_, i) => palette[i % palette.length]),
        borderWidth: 0,
      },
    ],
  };

  const barCatData = {
    labels: hoursByCat.labels,
    datasets: [
      {
        label: "Hours",
        data: hoursByCat.data,
        backgroundColor: "rgba(56, 189, 248, 0.55)",
        borderRadius: 6,
      },
    ],
  };

  const barCuData = {
    labels: hoursByCu.labels,
    datasets: [
      {
        label: "Hours",
        data: hoursByCu.data,
        backgroundColor: "rgba(167, 139, 250, 0.55)",
        borderRadius: 6,
      },
    ],
  };

  const barActData = {
    labels: topActivities.labels,
    datasets: [
      {
        label: "Hours",
        data: topActivities.data,
        backgroundColor: "rgba(251, 191, 36, 0.55)",
        borderRadius: 6,
      },
    ],
  };

  const axisCommon = {
    ticks: { color: chartColors.text },
    grid: { color: chartColors.grid },
  };

  return (
    <div className="cu-root">
      <aside className="cu-sidebar">
        <h1>Time survey analytics</h1>
        <p>
          Live metrics from <code style={{ color: "#5eead4" }}>kohl_direct</code> — surveys, hours, and
          approvals by credit union.
        </p>
        <button type="button" className="cu-back" onClick={onBack}>
          ← Finance dashboard
        </button>
        <p style={{ marginTop: "16px", fontSize: "0.78rem", color: "#64748b" }}>
          {user?.email}
        </p>
        <button
          type="button"
          className="cu-back"
          style={{ marginTop: "8px" }}
          onClick={logout}
        >
          Sign out
        </button>
        <p style={{ marginTop: "16px", fontSize: "0.72rem", color: "#475569" }}>
          API: {apiBase}
        </p>
      </aside>

      <main className="cu-main">
        <header className="cu-hero">
          <div>
            <h2>Credit union dashboard</h2>
            <p className="cu-hero-sub">
              Filter by institution and time period, explore workload distribution, and export filtered CSVs for
              audits or planning.
            </p>
          </div>
        </header>

        {error && <div className="cu-error">{String(error)}</div>}

        <section className="cu-panel">
          <h3 className="cu-panel-title">Filters</h3>
          <div className="cu-filters">
            <select
              className="cu-select"
              name="cu_id"
              value={filters.cu_id}
              onChange={handleFilterChange}
              aria-label="Credit union"
            >
              <option value="">All credit unions</option>
              {creditUnions.map((cu) => (
                <option key={cu.id} value={String(cu.id)}>
                  {cu.name}
                  {cu.shortname ? ` (${cu.shortname})` : ""}
                </option>
              ))}
            </select>
            <input
              className="cu-input"
              type="date"
              name="start"
              value={filters.start}
              onChange={handleFilterChange}
              aria-label="Start date"
            />
            <input
              className="cu-input"
              type="date"
              name="end"
              value={filters.end}
              onChange={handleFilterChange}
              aria-label="End date"
            />
            <select
              className="cu-select"
              name="approved"
              value={filters.approved}
              onChange={handleFilterChange}
              aria-label="Approval"
            >
              <option value="">Approval: any</option>
              <option value="true">Approved only</option>
              <option value="false">Not approved</option>
            </select>
            <button
              type="button"
              className="cu-btn cu-btn-primary"
              onClick={refresh}
              disabled={loading}
            >
              {loading ? "Loading…" : "Apply filters"}
            </button>
          </div>

          <details
            className="cu-advanced"
            open={showAdvanced}
            onToggle={(e) => setShowAdvanced(e.target.open)}
          >
            <summary>Advanced filters</summary>
            <div className="cu-filters" style={{ marginTop: 12 }}>
              <select
                className="cu-select"
                name="completed"
                value={filters.completed}
                onChange={handleFilterChange}
                aria-label="Completion"
              >
                <option value="">Completion: any</option>
                <option value="true">Completed only</option>
                <option value="false">In progress only</option>
              </select>
              <input
                className="cu-input"
                style={{ minWidth: 220 }}
                name="employee_q"
                value={filters.employee_q}
                onChange={handleFilterChange}
                placeholder="Employee name, number, or department"
              />
            </div>
          </details>

          <div className="cu-exports">
            <span style={{ width: "100%", fontSize: "0.75rem", color: "#64748b", marginTop: 4 }}>
              Export (respects filters above)
            </span>
            <button
              type="button"
              className="cu-btn cu-btn-ghost"
              disabled={!!exporting}
              onClick={() => downloadExport("/api/cu/export/surveys", "surveys.csv")}
            >
              {exporting === "surveys.csv" ? "…" : "Surveys CSV"}
            </button>
            <button
              type="button"
              className="cu-btn cu-btn-ghost"
              disabled={!!exporting}
              onClick={() => downloadExport("/api/cu/export/answers", "survey_answers.csv")}
            >
              {exporting === "survey_answers.csv" ? "…" : "Line-level answers CSV"}
            </button>
            <button
              type="button"
              className="cu-btn cu-btn-ghost"
              disabled={!!exporting}
              onClick={() => downloadExport("/api/cu/export/employees", "employees.csv")}
            >
              {exporting === "employees.csv" ? "…" : "Employees CSV"}
            </button>
            <button
              type="button"
              className="cu-btn cu-btn-ghost"
              disabled={!!exporting}
              onClick={() => downloadExport("/api/cu/export/activities", "activities_catalog.csv")}
            >
              {exporting === "activities_catalog.csv" ? "…" : "Activity catalog CSV"}
            </button>
          </div>
        </section>

        <div className="cu-kpi-grid">
          <div className="cu-kpi">
            <div className="cu-kpi-label">Total surveys</div>
            <div className="cu-kpi-value">{fmtInt(summary.total_surveys)}</div>
          </div>
          <div className="cu-kpi">
            <div className="cu-kpi-label">Completed</div>
            <div className="cu-kpi-value">{fmtInt(summary.completed_surveys)}</div>
          </div>
          <div className="cu-kpi">
            <div className="cu-kpi-label">Approved</div>
            <div className="cu-kpi-value">{fmtInt(summary.approved_surveys)}</div>
          </div>
          <div className="cu-kpi">
            <div className="cu-kpi-label">Pending approval</div>
            <div className="cu-kpi-value">{fmtInt(summary.pending_approval)}</div>
          </div>
          <div className="cu-kpi">
            <div className="cu-kpi-label">Unique employees</div>
            <div className="cu-kpi-value">{fmtInt(summary.unique_employees)}</div>
          </div>
          <div className="cu-kpi">
            <div className="cu-kpi-label">CUs in scope</div>
            <div className="cu-kpi-value">{fmtInt(summary.credit_unions_represented)}</div>
          </div>
          <div className="cu-kpi">
            <div className="cu-kpi-label">Hours reported</div>
            <div className="cu-kpi-value">{fmtHours(summary.total_hours_reported)}</div>
          </div>
        </div>

        <div className="cu-charts">
          <div className="cu-chart-card">
            <h3>Surveys started over time</h3>
            {timeline.labels.length > 0 ? (
              <Line
                data={lineData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: axisCommon,
                    y: { ...axisCommon, beginAtZero: true },
                  },
                }}
                style={{ maxHeight: 280 }}
              />
            ) : (
              <p className="cu-empty">No survey starts in this range.</p>
            )}
          </div>
          <div className="cu-chart-card">
            <h3>Status mix</h3>
            {approval.labels.length > 0 ? (
              <Doughnut
                data={doughnutData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { position: "right" } },
                }}
                style={{ maxHeight: 280 }}
              />
            ) : (
              <p className="cu-empty">No surveys match the filters.</p>
            )}
          </div>
        </div>

        <div className="cu-charts">
          <div className="cu-chart-card">
            <h3>Hours by activity category</h3>
            {hoursByCat.labels.length > 0 ? (
              <Bar
                data={barCatData}
                options={{
                  indexAxis: "y",
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { ...axisCommon, beginAtZero: true },
                    y: axisCommon,
                  },
                }}
                style={{ maxHeight: 320 }}
              />
            ) : (
              <p className="cu-empty">No hours in this range.</p>
            )}
          </div>
          <div className="cu-chart-card">
            <h3>Hours by credit union</h3>
            {hoursByCu.labels.length > 0 ? (
              <Bar
                data={barCuData}
                options={{
                  indexAxis: "y",
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { ...axisCommon, beginAtZero: true },
                    y: axisCommon,
                  },
                }}
                style={{ maxHeight: 320 }}
              />
            ) : (
              <p className="cu-empty">No hours in this range.</p>
            )}
          </div>
        </div>

        <div className="cu-chart-card" style={{ marginBottom: 22 }}>
          <h3>Top activities by hours</h3>
          {topActivities.labels.length > 0 ? (
            <Bar
              data={barActData}
              options={{
                indexAxis: "y",
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                  x: { ...axisCommon, beginAtZero: true },
                  y: { ...axisCommon, ticks: { ...axisCommon.ticks, autoSkip: false } },
                },
              }}
              style={{ maxHeight: 380 }}
            />
          ) : (
            <p className="cu-empty">No activity hours in this range.</p>
          )}
        </div>

        <section className="cu-panel" style={{ padding: 0, overflow: "hidden" }}>
          <h3 className="cu-panel-title" style={{ padding: "18px 20px 0" }}>
            Recent surveys
          </h3>
          <div className="cu-table-wrap">
            <table className="cu-table">
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Employee</th>
                  <th>Credit union</th>
                  <th>Hours</th>
                  <th>Done</th>
                  <th>Approved</th>
                </tr>
              </thead>
              <tbody>
                {surveys.map((row) => (
                  <tr key={row.id}>
                    <td>{fmtTs(row.date_started)}</td>
                    <td>
                      <div>{row.employee_name}</div>
                      <div style={{ fontSize: "0.72rem", color: "#94a3b8" }}>
                        #{row.employee_number}
                      </div>
                    </td>
                    <td>{row.cu_name || "—"}</td>
                    <td>{fmtHours(row.hours_total)}</td>
                    <td>{row.date_completed ? fmtTs(row.date_completed) : "—"}</td>
                    <td>
                      {row.approved ? (
                        <span className="cu-pill cu-pill-yes">Yes</span>
                      ) : (
                        <span className="cu-pill cu-pill-no">No</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {surveys.length === 0 && !loading && (
              <p className="cu-empty">No surveys for these filters.</p>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
